import { BUILT_IN_THEMES, DEFAULT_THEME_ID, type ColorScheme, type ThemeDto } from '@kanban/shared';
import { useQuery } from '@tanstack/react-query';
import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { api } from './api.js';

/**
 * Farbschema (SPEC 8.3): hell, dunkel oder Systemeinstellung.
 *
 * Die Wahl liegt in `localStorage` UND auf dem Server. Das ist keine
 * Doppelung aus Bequemlichkeit: der Server ist die Wahrheit ueber Geraete
 * hinweg, aber nur `localStorage` steht vor dem ersten Paint zur Verfuegung.
 * Muesste die App erst `/auth/me` abwarten, gaebe es bei jedem Laden ein
 * weisses Aufblitzen — genau das, was 8.3 ausschliesst.
 */
export const THEME_STORAGE_KEY = 'kanban.theme';

/**
 * Gewaehltes Theme und seine Token, damit das Inline-Script in `index.html`
 * sie VOR dem ersten Paint setzen kann (SPEC 8.4, Abnahme A7). Ohne diesen
 * Zwischenspeicher saehe man nach jedem Laden kurz das Standardthema — genau
 * das Aufblitzen, das A7 ausschliesst.
 */
export const THEME_ID_STORAGE_KEY = 'kanban.theme.id';
export const THEME_TOKENS_STORAGE_KEY = 'kanban.theme.tokens';

function systemPrefersDark(): boolean {
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

export function resolveScheme(choice: ColorScheme): 'light' | 'dark' {
  if (choice === 'system') return systemPrefersDark() ? 'dark' : 'light';
  return choice;
}

function readStored(): ColorScheme {
  try {
    const value = localStorage.getItem(THEME_STORAGE_KEY);
    if (value === 'light' || value === 'dark' || value === 'system') return value;
  } catch {
    // Privater Modus oder blockierter Speicher — dann eben die Systemwahl.
  }
  return 'system';
}

/** Setzt `data-theme` und haelt `color-scheme` fuer Formularelemente nach. */
function apply(choice: ColorScheme): void {
  const resolved = resolveScheme(choice);
  document.documentElement.dataset.theme = resolved;

  // Ohne das bleiben Eingabefelder, Auswahlfelder und Scrollbalken hell,
  // auch wenn die Flaechen darum herum dunkel sind.
  document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', resolved);
}

/**
 * Token eines Themes auf `:root` setzen.
 *
 * Zurueckgenommen wird ueber die Liste der zuletzt gesetzten Namen: wer von
 * „Wald" auf „Standard" wechselt, bekaeme sonst ein Standardthema mit
 * gruenen Resten — Custom Properties verschwinden nicht von selbst.
 */
let appliedTokens: string[] = [];

function applyTokens(tokens: Record<string, string>): void {
  const root = document.documentElement;
  for (const name of appliedTokens) {
    if (!(name in tokens)) root.style.removeProperty(name);
  }
  for (const [name, value] of Object.entries(tokens)) root.style.setProperty(name, value);
  appliedTokens = Object.keys(tokens);
}

function cacheTheme(theme: ThemeDto | undefined): void {
  try {
    if (!theme || theme.id === DEFAULT_THEME_ID) {
      localStorage.removeItem(THEME_TOKENS_STORAGE_KEY);
      return;
    }
    localStorage.setItem(
      THEME_TOKENS_STORAGE_KEY,
      JSON.stringify({ light: theme.light, dark: theme.dark }),
    );
  } catch {
    // Privater Modus: dann blitzt es beim naechsten Laden einmal kurz auf.
    // Kein Grund, die Umschaltung scheitern zu lassen.
  }
}

/**
 * Verfuegbare Themes (SPEC 8.4).
 *
 * Die mitgelieferten stehen sofort zur Verfuegung; die Abfrage ergaenzt, was
 * auf dem Server abgelegt wurde. So ist die Auswahl nie leer, auch wenn der
 * Endpoint gerade nicht antwortet.
 */
export function useThemes() {
  return useQuery({
    queryKey: ['themes'],
    queryFn: () => api.get<{ themes: ThemeDto[] }>('/themes').then((r) => r.themes),
    initialData: BUILT_IN_THEMES.map((theme) => ({ ...theme, source: 'eingebaut' as const })),
    staleTime: 5 * 60_000,
  });
}

interface ThemeValue {
  choice: ColorScheme;
  resolved: 'light' | 'dark';
  change: (next: ColorScheme, userId: string) => void;
  /** Gewaehltes Theme (SPEC 8.4) — unabhaengig von hell/dunkel. */
  themeId: string;
  chooseTheme: (id: string, userId: string) => void;
}

const ThemeContext = createContext<ThemeValue | null>(null);

/**
 * Ein Provider statt mehrerer Hook-Instanzen: zwei unabhaengige Zustaende
 * wuerden auseinanderlaufen, sobald das Profil geschlossen wird — das Board
 * bliebe dann im alten Schema stehen.
 */
export function ThemeProvider({
  serverChoice,
  serverThemeId,
  children,
}: {
  serverChoice: ColorScheme | undefined;
  serverThemeId: string | undefined;
  children: ReactNode;
}) {
  const value = useColorSchemeState(serverChoice, serverThemeId);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeValue {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme benoetigt einen ThemeProvider');
  return value;
}

function readStoredThemeId(): string {
  try {
    return localStorage.getItem(THEME_ID_STORAGE_KEY) ?? DEFAULT_THEME_ID;
  } catch {
    return DEFAULT_THEME_ID;
  }
}

function useColorSchemeState(
  serverChoice: ColorScheme | undefined,
  serverThemeId: string | undefined,
): ThemeValue {
  const [choice, setChoice] = useState<ColorScheme>(readStored);
  const [themeId, setThemeId] = useState<string>(readStoredThemeId);
  const themes = useThemes();

  // Serverwert gewinnt, sobald er da ist — er gilt ueber alle Geraete.
  useEffect(() => {
    if (!serverChoice || serverChoice === choice) return;
    setChoice(serverChoice);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, serverChoice);
    } catch {
      // ignorieren
    }
    // Absichtlich ohne `choice` in den Abhaengigkeiten: sonst wuerde eine
    // lokale Umstellung sofort wieder vom Serverwert ueberschrieben.
  }, [serverChoice]);

  // Serverwert des Themes gewinnt, sobald er da ist — wie beim Farbschema.
  useEffect(() => {
    if (!serverThemeId || serverThemeId === themeId) return;
    setThemeId(serverThemeId);
    try {
      localStorage.setItem(THEME_ID_STORAGE_KEY, serverThemeId);
    } catch {
      // ignorieren
    }
    // Ohne `themeId` in den Abhaengigkeiten: sonst zoege der Serverwert eine
    // gerade getroffene lokale Wahl sofort wieder zurueck.
  }, [serverThemeId]);

  useEffect(() => {
    apply(choice);
  }, [choice]);

  /*
   * Token setzen, sobald Theme oder Farbschema wechselt. Das laeuft bei jedem
   * Wechsel neu, auch wenn nur das Systemschema umspringt — die Token eines
   * Themes unterscheiden sich zwischen hell und dunkel.
   */
  const resolved = resolveScheme(choice);
  const active = themes.data?.find((theme) => theme.id === themeId);

  useEffect(() => {
    applyTokens(active ? (resolved === 'dark' ? active.dark : active.light) : {});
    cacheTheme(active);
  }, [active, resolved]);

  // Bei "Systemeinstellung" auf Wechsel reagieren, ohne Reload.
  useEffect(() => {
    if (choice !== 'system') return;
    const media = window.matchMedia?.('(prefers-color-scheme: dark)');
    if (!media) return;
    const onChange = () => apply('system');
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [choice]);

  const change = useCallback((next: ColorScheme, userId: string) => {
    setChoice(next);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, next);
    } catch {
      // ignorieren
    }
    // Serverseitig nachziehen, aber die Anzeige nicht darauf warten lassen.
    void api.patch(`/users/${userId}`, { colorScheme: next }).catch(() => undefined);
  }, []);

  const chooseTheme = useCallback((id: string, userId: string) => {
    setThemeId(id);
    try {
      localStorage.setItem(THEME_ID_STORAGE_KEY, id);
    } catch {
      // ignorieren
    }
    void api.patch(`/users/${userId}`, { themeId: id }).catch(() => undefined);
  }, []);

  return { choice, resolved, change, themeId, chooseTheme };
}
